/**
 * influenceAndCareNetwork.js
 *
 * Infers WHO is sustaining WHOM in the pastoral context.
 * Detects imbalances in the care/support network.
 *
 * NOT a social graph. NOT network analysis software.
 * Just soft pastoral inference from language:
 * "Who is carrying too much? Who is isolated? Who is the unacknowledged sustainer?"
 *
 * Patterns:
 *   sobrecarga_cuidador      — one person carrying others' weight invisibly
 *   aislamiento              — someone with no one caring for them
 *   dependencia_excesiva     — unhealthy emotional/spiritual dependency
 *   influencia_positiva      — someone positively anchoring others
 *   ninguno                  — no imbalance signal
 */

const IMBALANCE_PATTERNS = [
  {
    pattern: "sobrecarga_cuidador",
    test: (t) =>
      /(?:cuida|sostiene|apoya)\s+a\s+todos\s+(?:pero\s+nadie|sin\s+que\s+nadie)\s+(?:la|lo|cuida|sostiene|apoya)/i.test(t) ||
      /es\s+quien\s+(?:mantiene|sostiene|cuida)\s+(?:a\s+)?toda\s+la\s+(?:familia|iglesia|grupo)/i.test(t) ||
      /(?:nadie\s+le\s+(?:pregunta\s+)?cómo\s+(?:está|se\s+siente))\s+a\s+(?:él|ella)/i.test(t) ||
      /(?:siempre\s+)?(?:dando|ayudando|sosteniendo)\s+a\s+otros\s+(?:pero|sin)\s+(?:recibir|descansar)/i.test(t),
  },
  {
    pattern: "aislamiento",
    test: (t) =>
      /(?:no\s+(?:tiene|hay)\s+(?:nadie|personas?|alguien))\s+que\s+(?:lo|la|le)\s+(?:apoye|acompañe|visite|cuide)/i.test(t) ||
      /(?:completamente|totalmente)\s+(?:solo|sola|aislad[ao])\s+(?:sin|y\s+sin)/i.test(t) ||
      /no\s+(?:tiene|hay)\s+red\s+de\s+apoyo/i.test(t),
  },
  {
    pattern: "dependencia_excesiva",
    test: (t) =>
      /(?:solo\s+quiere\s+hablar\s+con|solo\s+confía\s+en)\s+(?:el\s+pastor|yo|mí)/i.test(t) ||
      /(?:necesita\s+)?llamar(?:me)?\s+todos?\s+los?\s+(?:días|semanas)/i.test(t) ||
      /(?:no\s+puede\s+tomar\s+ninguna\s+decisión|consulta\s+todo)\s+(?:sin|conmigo|con\s+el\s+pastor)/i.test(t),
  },
  {
    pattern: "influencia_positiva",
    test: (t) =>
      /es\s+un\s+(?:pilar|ancla|soporte|sostén)\s+(?:para|de)\s+(?:la\s+familia|la\s+iglesia|el\s+grupo|muchos)/i.test(t) ||
      /(?:sostiene|cuida|anima)\s+a\s+(?:muchos|varios|toda\s+la\s+(?:familia|congregación))/i.test(t) ||
      /tiene\s+(?:mucha\s+)?influencia\s+(?:positiva|espiritual)\s+(?:sobre|en)/i.test(t),
  },
];

/**
 * @param {{
 *   text:          string,
 *   emotion:       object|null,
 *   intent:        string,
 *   entityContext: object|null,
 * }} params
 * @returns {{
 *   hasImbalance:  boolean,
 *   pattern:       string,
 *   instruction:   string|null,
 * }}
 */
export function inferCareNetwork({ text, emotion, intent, entityContext }) {
  const t = text ?? "";

  for (const { pattern, test } of IMBALANCE_PATTERNS) {
    if (test(t)) {
      return { hasImbalance: pattern !== "influencia_positiva", pattern, instruction: _instruction(pattern) };
    }
  }

  return { hasImbalance: false, pattern: "ninguno", instruction: null };
}

function _instruction(pattern) {
  if (pattern === "sobrecarga_cuidador") {
    return "Hay alguien cargando con el cuidado de muchos sin que nadie cuide de esa persona. Reconoce esa carga invisible.";
  }
  if (pattern === "aislamiento") {
    return "Hay aislamiento relacional. Esta persona no tiene quién la sostenga. Eso cambia el tipo de acompañamiento que necesita.";
  }
  if (pattern === "dependencia_excesiva") {
    return "Hay señales de dependencia pastoral excesiva. Acompaña con límites sanos — sin rechazo, pero fomentando autonomía.";
  }
  if (pattern === "influencia_positiva") {
    return "Esta persona tiene influencia positiva en otros. Reconoce su rol — también ellos necesitan ser cuidados.";
  }
  return null;
}
